import React from "react";
import izenuAPI from '../../services/izenuAPI';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import { Doughnut, Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale, LinearScale, BarElement, PointElement,
  LineElement, ArcElement, Title, Tooltip, Legend
);

const CDN = 'https://dpagz47n4g1lw.cloudfront.net';

const MONTH_NAMES = ["Janeiro", "Fevereiro", "Março", "Abril", "Maio", "Junho",
  "Julho", "Agosto", "Setembro", "Outubro", "Novembro", "Dezembro"];

const d = new Date();
const m3 = MONTH_NAMES[(d.getMonth() - 2 + 12) % 12];
const m2 = MONTH_NAMES[(d.getMonth() - 1 + 12) % 12];
const m1 = MONTH_NAMES[d.getMonth()];

const GENRE_COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];
const COUNTRY_COLORS = ['#6366f1', '#f43f5e', '#f59e0b', '#10b981', '#3b82f6', '#8b5cf6', '#ec4899', '#14b8a6'];

function fmt(n) {
  if (!n) return '0';
  const num = Number(n);
  if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
  if (num >= 1000) return (num / 1000).toFixed(1) + 'K';
  return num.toLocaleString('pt-PT');
}

function getCoverUrl(artistId, cover) {
  if (!cover) return null;
  if (cover.startsWith('http')) return cover;
  if (cover.startsWith('release_')) return `${CDN}/users/${artistId}/releases/${cover}`;
  return `${CDN}/songs/artworks/${cover}`;
}

function getPhoto(userId, photo) {
  if (!photo) return null;
  if (photo.startsWith('http')) return photo;
  return `${CDN}/users/${userId}/${photo}`;
}

function timeAgo(dateStr) {
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return 'agora';
  if (diff < 3600) return Math.floor(diff / 60) + 'min';
  if (diff < 86400) return Math.floor(diff / 3600) + 'h';
  if (diff < 604800) return Math.floor(diff / 86400) + 'd';
  return date.toLocaleDateString('pt-PT');
}

const accTypeLabels = { artist: 'Artista', creator: 'Criador', listener: 'Ouvinte' };
const accTypeColors = { artist: '#10b981', creator: '#3b82f6', listener: '#8b5cf6' };

function StatCard({ label, value, sub, color = '#6366f1', icon }) {
  return (
    <div style={s.statCard}>
      <div style={{ ...s.statIcon, backgroundColor: color + '15', color }}>{icon}</div>
      <div style={{ flex: 1 }}>
        <p style={s.statLabel}>{label}</p>
        <h2 style={{ ...s.statValue, color }}>{value}</h2>
        {sub && <p style={s.statSub}>{sub}</p>}
      </div>
    </div>
  );
}

export default function DashboardOperations() {
  const [data, setData] = React.useState(null);

  React.useEffect(() => {
    (async () => {
      try {
        const { data: r } = await izenuAPI.get('/admin/dashboard/fetchDashboardData.php');
        setData(r);
      } catch (err) { console.log(err); }
    })();
  }, []);

  if (!data) {
    return <div style={{ padding: 40, textAlign: 'center', color: '#888' }}>A carregar dashboard...</div>;
  }

  const md = data.months_data || {};

  // User trend bar chart
  const trendData = {
    labels: [m3, m2, m1],
    datasets: [
      { label: 'Artistas', data: [md.artists?.m3 || 0, md.artists?.m2 || 0, md.artists?.m1 || 0], backgroundColor: '#10b981', borderRadius: 4 },
      { label: 'Criadores', data: [md.creators?.m3 || 0, md.creators?.m2 || 0, md.creators?.m1 || 0], backgroundColor: '#3b82f6', borderRadius: 4 },
      { label: 'Ouvintes', data: [md.listener?.m3 || 0, md.listener?.m2 || 0, md.listener?.m1 || 0], backgroundColor: '#8b5cf6', borderRadius: 4 },
    ]
  };
  const trendOpts = {
    responsive: true, maintainAspectRatio: false,
    plugins: { legend: { position: 'top', labels: { boxWidth: 12, padding: 14, font: { size: 12 } } } },
    scales: { x: { grid: { display: false } }, y: { grid: { color: '#f3f4f6' }, beginAtZero: true } }
  };

  // User distribution doughnut
  const userDistData = {
    labels: ['Ouvintes', 'Artistas', 'Criadores'],
    datasets: [{
      data: [data.listeners || 0, data.artists || 0, data.creators || 0],
      backgroundColor: ['#8b5cf6', '#10b981', '#3b82f6'],
      borderWidth: 0, hoverOffset: 6
    }]
  };
  const userDistOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: { legend: { position: 'right', labels: { boxWidth: 12, padding: 12, font: { size: 12 } } } }
  };

  // Genre doughnut
  const genres = (data.streams_by_genre || []).filter(g => g.genre && g.genre !== '');
  const genreData = {
    labels: genres.map(g => g.genre),
    datasets: [{ data: genres.map(g => Number(g.streams)), backgroundColor: GENRE_COLORS, borderWidth: 0, hoverOffset: 6 }]
  };
  const genreOpts = {
    responsive: true, maintainAspectRatio: false, cutout: '65%',
    plugins: { legend: { position: 'right', labels: { boxWidth: 12, padding: 10, font: { size: 11 } } } }
  };

  // Country horizontal bar
  const countries = (data.streams_by_country || []).filter(c => c.country && c.country !== 'unknown').sort((a, b) => b.total - a.total).slice(0, 8);
  const countryData = {
    labels: countries.map(c => c.country),
    datasets: [{ data: countries.map(c => Number(c.total)), backgroundColor: COUNTRY_COLORS, borderRadius: 4, barThickness: 20 }]
  };
  const countryOpts = {
    indexAxis: 'y', responsive: true, maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { x: { grid: { display: false }, ticks: { font: { size: 11 }, color: '#888' } }, y: { grid: { display: false }, ticks: { font: { size: 12 }, color: '#333' } } }
  };

  return (
    <div style={s.container}>
      <div style={s.header}>
        <div>
          <h1 style={s.title}>Dashboard Admin</h1>
          <p style={s.subtitle}>Visão geral da plataforma Izenu</p>
        </div>
      </div>

      {/* Row 1: User stats */}
      <div style={s.row}>
        <StatCard label="Utilizadores" value={fmt(data.users)} sub={`+${data.new_users_7d || 0} últimos 7 dias`} color="#6366f1" icon="👥" />
        <StatCard label="Artistas" value={fmt(data.artists)} sub={`${data.avg_songs_per_artist || 0} músicas/artista`} color="#10b981" icon="🎤" />
        <StatCard label="Criadores" value={fmt(data.creators)} color="#3b82f6" icon="🎬" />
        <StatCard label="Ouvintes" value={fmt(data.listeners)} color="#8b5cf6" icon="🎧" />
      </div>

      {/* Row 2: Content stats */}
      <div style={s.row}>
        <StatCard label="Músicas" value={fmt(data.total_songs)} sub={data.new_songs_7d > 0 ? `+${data.new_songs_7d} últimos 7 dias` : null} color="#f59e0b" icon="🎵" />
        <StatCard label="Streams" value={fmt(data.total_streams)} color="#f43f5e" icon="▶" />
        <StatCard label="Plays" value={fmt(data.total_plays)} color="#ec4899" icon="🔊" />
        <StatCard label="Premium" value={fmt(data.premium_active)} sub="subscrições ativas" color="#14b8a6" icon="⭐" />
      </div>

      {/* Row 3: More content */}
      <div style={s.row}>
        <StatCard label="Releases" value={fmt(data.total_releases)} color="#6366f1" icon="💿" />
        <StatCard label="Playlists" value={fmt(data.total_playlists)} color="#10b981" icon="📋" />
      </div>

      {/* Row 4: User trend + User distribution */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Novos utilizadores — últimos 3 meses</h3>
          <p style={s.cardDesc}>Evolução mensal de novos registos por tipo de conta (artistas, criadores e ouvintes).</p>
          <div style={{ height: 260 }}>
            <Bar data={trendData} options={trendOpts} />
          </div>
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Distribuição de utilizadores</h3>
          <p style={s.cardDesc}>Proporção entre ouvintes, artistas e criadores no total de contas da plataforma.</p>
          <div style={{ height: 260 }}>
            <Doughnut data={userDistData} options={userDistOpts} />
          </div>
        </div>
      </div>

      {/* Row 5: Genre + Country */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Streams por Género</h3>
          <p style={s.cardDesc}>Géneros musicais mais ouvidos na plataforma, baseado em streams qualificados (30s+).</p>
          {genres.length > 0 ? (
            <div style={{ height: 240 }}><Doughnut data={genreData} options={genreOpts} /></div>
          ) : <p style={s.empty}>Sem dados</p>}
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Streams por País</h3>
          <p style={s.cardDesc}>Distribuição geográfica dos ouvintes, obtida via geolocalização no momento do stream.</p>
          {countries.length > 0 ? (
            <div style={{ height: 240 }}><Bar data={countryData} options={countryOpts} /></div>
          ) : <p style={s.empty}>Sem dados</p>}
        </div>
      </div>

      {/* Row 6: Top Songs + Top Artists */}
      <div style={s.row}>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Top Músicas</h3>
          <p style={s.cardDesc}>As 5 músicas mais ouvidas em toda a plataforma por total de streams.</p>
          {(data.top_songs || []).map((song, i) => (
            <div key={song.music_id} style={s.listItem}>
              <span style={s.rank}>#{i + 1}</span>
              <img src={getCoverUrl(song.artist_id, song.cover)} alt="" style={s.coverImg} onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.itemTitle}>{song.title}</p>
                <p style={s.itemSub}>{song.artist_name}</p>
              </div>
              <span style={s.streamBadge}>{fmt(song.streams)}</span>
            </div>
          ))}
        </div>
        <div style={s.card}>
          <h3 style={s.cardTitle}>Top Artistas</h3>
          <p style={s.cardDesc}>Os 5 artistas com maior volume de streams acumulados.</p>
          {(data.top_artists || []).map((a, i) => (
            <div key={a.user_id} style={s.listItem}>
              <span style={s.rank}>#{i + 1}</span>
              <img src={getPhoto(a.user_id, a.photo)} alt="" style={s.artistImg} onError={e => { e.target.style.display = 'none'; }} />
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={s.itemTitle}>{a.artist_name}</p>
                <p style={s.itemSub}>{a.number_of_followers || 0} seguidores</p>
              </div>
              <span style={s.streamBadge}>{fmt(a.streams)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Row 7: Recent Users */}
      <div style={s.row}>
        <div style={{ ...s.card, flex: '1 1 100%' }}>
          <h3 style={s.cardTitle}>Registos recentes</h3>
          <p style={s.cardDesc}>Últimos 10 utilizadores registados na plataforma, com tipo de conta e data.</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>Nome</th>
                  <th style={s.th}>Email</th>
                  <th style={s.th}>Tipo</th>
                  <th style={s.th}>Registado</th>
                </tr>
              </thead>
              <tbody>
                {(data.recent_users || []).map((u) => (
                  <tr key={u.user_id}>
                    <td style={s.td}>{u.artist_name || u.name || '—'}</td>
                    <td style={s.td}>{u.email || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.typeBadge, backgroundColor: (accTypeColors[u.acc_type] || '#888') + '18', color: accTypeColors[u.acc_type] || '#888' }}>
                        {accTypeLabels[u.acc_type] || u.acc_type}
                      </span>
                    </td>
                    <td style={s.td}>{timeAgo(u.time_stamp)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

const s = {
  container: { padding: '0 20px 40px 20px', maxWidth: 1300 },
  header: { marginBottom: 24 },
  title: { fontSize: 24, fontWeight: 700, color: '#1a1a2e', margin: 0 },
  subtitle: { margin: '4px 0 0 0', fontSize: 13, color: '#999', fontWeight: 400 },
  row: { display: 'flex', gap: 16, marginBottom: 20, flexWrap: 'wrap' },
  statCard: {
    flex: '1 1 200px', display: 'flex', alignItems: 'center', gap: 14,
    padding: '18px 20px', backgroundColor: '#fff', borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', minWidth: 180,
  },
  statIcon: {
    width: 42, height: 42, borderRadius: 10, display: 'flex',
    alignItems: 'center', justifyContent: 'center', fontSize: 19, flexShrink: 0,
  },
  statLabel: { margin: 0, fontSize: 12, color: '#888', fontWeight: 500 },
  statValue: { margin: '2px 0', fontSize: 24, fontWeight: 700, lineHeight: 1.2 },
  statSub: { margin: 0, fontSize: 11, color: '#aaa' },
  card: {
    flex: '1 1 400px', padding: 24, backgroundColor: '#fff', borderRadius: 12,
    boxShadow: '0 1px 8px rgba(0,0,0,0.06)', border: '1px solid #f0f0f0', minWidth: 320,
  },
  cardTitle: { fontSize: 15, fontWeight: 600, color: '#333', margin: '0 0 4px 0' },
  cardDesc: { margin: '0 0 14px 0', fontSize: 12, color: '#aaa', fontWeight: 400, lineHeight: 1.4 },
  listItem: { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: '1px solid #f5f5f5' },
  rank: { fontSize: 13, fontWeight: 700, color: '#bbb', width: 26, textAlign: 'center', flexShrink: 0 },
  coverImg: { width: 40, height: 40, borderRadius: 6, objectFit: 'cover', backgroundColor: '#f0f0f0', flexShrink: 0 },
  artistImg: { width: 40, height: 40, borderRadius: 20, objectFit: 'cover', backgroundColor: '#f0f0f0', flexShrink: 0 },
  itemTitle: { margin: 0, fontSize: 14, fontWeight: 600, color: '#222', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' },
  itemSub: { margin: 0, fontSize: 12, color: '#999' },
  streamBadge: { fontSize: 14, fontWeight: 600, color: '#6366f1', flexShrink: 0 },
  empty: { color: '#ccc', textAlign: 'center', padding: 40, fontSize: 14 },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 12px', borderBottom: '2px solid #f0f0f0', color: '#888', fontWeight: 600, fontSize: 12 },
  td: { padding: '10px 12px', borderBottom: '1px solid #f5f5f5', color: '#333' },
  typeBadge: { padding: '3px 10px', borderRadius: 12, fontSize: 11, fontWeight: 600 },
};
